import { type FC, useEffect, useState } from 'react';
import classNames from 'classnames';
import { motion, transform } from 'framer-motion';

import { HOLIDAY_KEY, PICTURES_CONFIG } from '@/common/config/media-config';
import { widthToPercentage } from '@/common/lib/size';

import { Logo } from '../ui/Icons';
import { MotionPicture } from '../ui/Picture/Picture';

import {
  calcTopDistanceForAnimationBlock,
  firstBlockAnimation,
  secondBlockAnimation,
  thirdBlockAnimation,
} from './config';

import css from './styles.module.scss';

const FirstLoadScreen: FC = () => {
  const [topDistance, setTopDistance] = useState<{
    first: number;
    second: number;
    third: number;
  } | null>(null);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setTopDistance({
        first: calcTopDistanceForAnimationBlock(0.3),
        second: calcTopDistanceForAnimationBlock(0.475),
        third: calcTopDistanceForAnimationBlock(0.6),
      });
    }, 100);

    return (): void => {
      clearTimeout(timerId);
    };
  }, []);

  const distanceToPercentage = {
    secondBlock: {
      calcByWidth: transform([0, 481], ['0%', '100%']),
      calcByHeight: transform([0, 191.68], ['0%', '100%']),
    },
    thirdBlock: {
      calcByWidth: transform([0, 509], ['0%', '100%']),
      calcByHeight: transform([0, 240], ['0%', '100%']),
    },
  };

  const scaleLogo = transform([0, 178], [0, 1]);

  return (
    <motion.div
      exit={{
        opacity: 0,
        position: 'absolute',
      }}
      className={classNames(
        css.firstLoadScreen,
        HOLIDAY_KEY === 'helloween' && css.helloween,
        HOLIDAY_KEY === 'new_year' && css.new_year,
      )}
    >
      <div className="absolute top-0 w-full flex justify-center">
        <motion.div layoutId="logo" className="absolute top-[100px]">
          <motion.div
            initial={{
              scale: scaleLogo(136),
              opacity: 0,
            }}
            animate={{
              scale: 1,
              opacity: 1,
            }}
            transition={{
              ease: 'easeIn',
              duration: 0.4,
              delay: 0.01,
            }}
          >
            <Logo className="w-[11.125rem]" />
          </motion.div>
        </motion.div>
      </div>
      {topDistance && (
        <>
          <motion.div
            id="first-items-block"
            initial={{
              y: '1.5rem',
              opacity: 0,
            }}
            animate={{
              y: 0,
              opacity: 1,
            }}
            transition={{
              ease: 'easeOut',
              duration: 0.4,
              delay: 0.42,
            }}
            style={{
              width: widthToPercentage(497.2),
              left: widthToPercentage(-31.2),
              top: topDistance.first,
            }}
            className={css.first}
          >
            <div className="w-full h-full relative">
              {firstBlockAnimation.map(({ png, webp, className }, index) => (
                <MotionPicture
                  className={className}
                  img={png}
                  webp={webp}
                  key={`first-${index}`}
                />
              ))}
            </div>
          </motion.div>
          <motion.div
            initial={{
              y: '2rem',
              opacity: 0,
            }}
            animate={{
              y: 0,
              opacity: 1,
            }}
            transition={{
              ease: 'easeOut',
              duration: 0.4,
              delay: 0.83,
            }}
            style={{
              width: widthToPercentage(481),
              left: widthToPercentage(-19),
              top: topDistance.second,
            }}
            className={css.second}
          >
            <div className="w-full h-full relative">
              {secondBlockAnimation.map((props, index) => (
                <MotionPicture
                  style={{
                    top:
                      props.top &&
                      distanceToPercentage.secondBlock.calcByHeight(props.top),
                    bottom:
                      props.bottom &&
                      distanceToPercentage.secondBlock.calcByHeight(
                        props.bottom,
                      ),
                    left:
                      props.left &&
                      distanceToPercentage.secondBlock.calcByWidth(props.left),
                    right:
                      props.right &&
                      distanceToPercentage.secondBlock.calcByWidth(props.right),
                  }}
                  img={props.png}
                  className={props.className}
                  key={`second-${index}`}
                />
              ))}
            </div>
          </motion.div>
          <motion.div
            initial={{
              y: '2rem',
              opacity: 0,
            }}
            animate={{
              y: 0,
              opacity: 1,
            }}
            transition={{
              ease: 'easeOut',
              duration: 0.4,
              delay: 1.24,
            }}
            style={{
              width: widthToPercentage(509),
              left: widthToPercentage(-37),
              top: topDistance.third,
            }}
            className={css.third}
          >
            <div className="w-full h-full relative">
              {thirdBlockAnimation.map((props, index) => (
                <MotionPicture
                  style={{
                    top:
                      props.top &&
                      distanceToPercentage.thirdBlock.calcByHeight(props.top),
                    bottom:
                      props.bottom &&
                      distanceToPercentage.thirdBlock.calcByHeight(
                        props.bottom,
                      ),
                    left:
                      props.left &&
                      distanceToPercentage.thirdBlock.calcByWidth(props.left),
                    right:
                      props.right &&
                      distanceToPercentage.thirdBlock.calcByWidth(props.right),
                  }}
                  img={props.png}
                  className={props.className}
                  key={`third-${index}`}
                />
              ))}
            </div>
          </motion.div>
        </>
      )}
      <MotionPicture
        initial={{
          y: '1.5rem',
        }}
        animate={{
          y: 0,
        }}
        transition={{
          ease: 'easeIn',
          duration: 0.4,
          delay: 0.01,
        }}
        className="absolute left-0 bottom-0 w-full aspect-[428/342]"
        img={PICTURES_CONFIG.FIRST_LOAD_CHARACTERS.png}
        webp={PICTURES_CONFIG.FIRST_LOAD_CHARACTERS.webp}
        alt="characters"
      />
    </motion.div>
  );
};

export { FirstLoadScreen };
